import type { ButtonHTMLAttributes } from 'react';
import { Icon } from '../icons';
import type { IconName } from '../icons';

/**
 * The action hierarchy (CO-88, audit B1/B3/B4). Buttons stay CSS classes on
 * native elements so links and submit buttons keep their semantics:
 *
 * | Class                    | Use                                                         |
 * |--------------------------|-------------------------------------------------------------|
 * | `button` / `--primary`   | The main action of a form or dialog: its submit button.     |
 * | `button--secondary`      | Every other action, including section and row actions.      |
 * | `button--ghost`          | Low-emphasis actions inside dense rows and toolbars.        |
 * | `button--danger-quiet`   | A destructive action that opens a confirmation.             |
 * | `button--danger`         | The confirm button of a destructive confirmation only.      |
 * | `button--small`          | Rows, toolbars and cards (28 px); default is 32 px.         |
 * | `link-button`            | An action inside a sentence, nowhere else.                  |
 * | `IconButton`             | An icon-only action; always named by `label`.               |
 *
 * A selected state is never shown with a primary button: use `Segmented`.
 *
 * What counts as the main action (CO-176, CO-178): the submit at the end of a
 * form (Save, Create, Connect, Install) and a dialog's confirm are primary, at
 * the default size, so every form on every page ends the same way. Everything
 * else is secondary: what acts on a row, a card or the page header (Send,
 * Download, Test, Sync stock, Retry, Approve), what runs a check or an action
 * on something that exists (Purge URL, Measure again), and a button beside one
 * field that acts on its value (Store key, Save retention, a Field `action`).
 * A small primary button is therefore always a mistake, and so is a secondary
 * submit that says Save, Create or Connect, and so is a filled danger button
 * that merely opens a confirmation (that is `--danger-quiet`; the filled one
 * is the confirm button inside the dialog); `controlGuards.test.ts` fails on
 * all three. Paging is `Pager`, never two loose buttons; filter chips and the help
 * tip (a ghost icon button) share the buttons' corner radius.
 *
 * So "one primary" means one per form or dialog, not one per page. A page
 * that is not a form has one main action of its own: in the project area it is
 * the header's Deploy. Section actions (Deploy, Back up now, Add frontend) are
 * secondary, and an empty section shows its own next step instead of repeating
 * the section action (audit B1). While setup is incomplete Deploy is disabled
 * and the setup banner's action ('Review the plan') is the page's primary
 * instead. The form on a page (a settings card) ends with its own primary
 * Save, which is why a settings page shows Deploy and a Save together.
 * A disabled action says why in its `title`.
 */
export function IconButton({
  icon,
  label,
  variant = 'default',
  size = 'md',
  className,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'aria-label'> & {
  icon: IconName;
  /** The accessible name and the tooltip. */
  label: string;
  variant?: 'default' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
}) {
  return (
    <button
      type="button"
      className={`icon-button${variant !== 'default' ? ` icon-button--${variant}` : ''}${size === 'sm' ? ' icon-button--sm' : ''}${className ? ` ${className}` : ''}`}
      aria-label={label}
      title={label}
      {...rest}
    >
      <Icon name={icon} size={16} />
    </button>
  );
}
